import React from "react";
import { LayoutDashboard, CalendarDays } from "lucide-react";
import moment from "moment-timezone";
import { toast } from "react-toastify";
import MasterPageShell from "../../components/masterData/MasterPageShell";
import { useAuthUser } from "../../hooks/useAuthUser";
import AdminApi from "../../api/adminApi";
import type { LoveStrongUser } from "../../types/api";
import type { OverviewData } from "../../types/dashboard";
import { emptyKpis, greetingForHour } from "./dashboard/format";
import { isLoggingOut, isMissingAccessToken } from "../../utils/authSession";
import KpiGrid from "./dashboard/KpiGrid";
import UpcomingRetreatsPanel from "./dashboard/UpcomingRetreatsPanel";
import LifecycleChart from "./dashboard/LifecycleChart";
import RegistrationsChart from "./dashboard/RegistrationsChart";
import NeedsAttentionPanel from "./dashboard/NeedsAttentionPanel";
import RecentActivityPanel from "./dashboard/RecentActivityPanel";
import QuickActions from "./dashboard/QuickActions";
import styles from "./Dashboard.module.scss";

const adminApi = new AdminApi();

function isDisabledFlag(value: unknown): boolean {
  return value === 1 || value === "1" || value === true;
}

const Dashboard = (): JSX.Element => {
  const userData = useAuthUser<LoveStrongUser>();
  const isFullAdmin =
    userData?.userRole === "ADMIN" && !isDisabledFlag(userData?.isDisabled);
  const [data, setData] = React.useState<OverviewData | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  const actor = React.useCallback(
    () => ({
      actorName: userData?.userName || "Admin",
      actorRole: userData?.userRole || "ADMIN",
      actorUserId: userData?.id || null,
      isAdmin: userData?.userRole === "ADMIN",
      isSuperAdmin: userData?.userRole === "ADMIN",
      accessId: userData?.id || null,
    }),
    [userData],
  );

  const load = React.useCallback(async () => {
    if (isLoggingOut()) return;
    setLoading(true);
    setError(null);
    try {
      const res: any = await adminApi.getOverview(actor());
      if (isLoggingOut()) return;
      if (res?.success && res.data) {
        setData(res.data as OverviewData);
      } else {
        const msg = res?.msg || "Failed to load overview.";
        if (isMissingAccessToken(msg)) return;
        setError(msg);
        toast.error(msg);
      }
    } catch (err: any) {
      const msg = err?.toString?.() || "Failed to load overview.";
      if (isMissingAccessToken(msg) || isLoggingOut()) return;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [actor]);

  React.useEffect(() => {
    load();
  }, [load]);

  const kpis = data?.kpis || emptyKpis();
  const firstName =
    (userData?.firstName as string) ||
    String(userData?.userName || "there").split(" ")[0];
  const todayLabel = moment().tz("America/New_York").format("dddd, MMMM D");

  return (
    <MasterPageShell>
      <div className={styles.page}>
        <div className={styles.hero}>
          <div className={styles.heroLeft}>
            <div className={styles.heroIcon}>
              <LayoutDashboard size={26} color="#285d8f" />
            </div>
            <div>
              <p className={styles.heroEyebrow}>
                <span className={styles.liveDot} />
                Live Overview
              </p>
              <h2 className={styles.heroTitle}>
                {greetingForHour()}, {firstName}
              </h2>
              <p className={styles.heroSubtitle}>
                A snapshot of retreats, couples, and the work that needs you today.
              </p>
            </div>
          </div>
          <div className={styles.heroDate}>
            <CalendarDays size={15} />
            {todayLabel}
          </div>
        </div>

        {error ? (
          <div className={styles.errorBanner}>
            <span>{error}</span>
            <button type="button" className={styles.retryBtn} onClick={load}>
              Retry
            </button>
          </div>
        ) : null}

        {loading && !data ? (
          <>
            <div className={styles.kpiGrid}>
              {Array.from({ length: isFullAdmin ? 6 : 5 }).map((_, i) => (
                <div key={i} className={`${styles.skeleton} ${styles.skeletonCard}`} />
              ))}
            </div>
            <div className={styles.mainGrid}>
              <div className={`${styles.skeleton} ${styles.skeletonChart}`} />
              <div className={`${styles.skeleton} ${styles.skeletonChart}`} />
            </div>
          </>
        ) : data ? (
          <>
            <KpiGrid kpis={kpis} showTickets={Boolean(isFullAdmin)} />

            <div className={styles.mainGrid}>
              <UpcomingRetreatsPanel retreats={data.upcomingRetreats || []} />
              <NeedsAttentionPanel items={data.needsAttention || []} />
            </div>

            <div className={styles.chartsGrid}>
              <LifecycleChart slices={data.lifecycle || []} />
              <RegistrationsChart months={data.registrationsByMonth || []} />
            </div>

            {isFullAdmin ? (
              <div className={styles.lowerGrid}>
                <RecentActivityPanel items={data.recentActivity || []} />
                <section className={styles.panel}>
                  <div className={styles.panelHeader}>
                    <div className={styles.panelTitleWrap}>
                      <span className={styles.panelGlyph}>
                        <LayoutDashboard size={16} />
                      </span>
                      <div>
                        <h3 className={styles.panelTitle}>Quick Actions</h3>
                        <p className={styles.panelKicker}>Jump into daily work</p>
                      </div>
                    </div>
                  </div>
                  <QuickActions isFullAdmin={isFullAdmin} />
                </section>
              </div>
            ) : (
              <section className={styles.panel}>
                <div className={styles.panelHeader}>
                  <div className={styles.panelTitleWrap}>
                    <span className={styles.panelGlyph}>
                      <LayoutDashboard size={16} />
                    </span>
                    <div>
                      <h3 className={styles.panelTitle}>Quick Actions</h3>
                      <p className={styles.panelKicker}>Jump into daily work</p>
                    </div>
                  </div>
                </div>
                <QuickActions isFullAdmin={false} />
              </section>
            )}
          </>
        ) : null}
      </div>
    </MasterPageShell>
  );
};

export default Dashboard;
